import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { Config } from './types';

const SESSION_DISMISS_KEY_PREFIX = 'banner_dismissed_';

const getSessionStorageValue = (key: string): string | null => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSessionStorageValue = (key: string, value: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Ignore quota/security errors and continue rendering.
  }
};

const StyledBannerWidget = styled.div``;

const StickyBannerWrapper = styled.div<{
  $height: number;
  $fullWidth?: boolean;
}>`
  position: fixed;
  bottom: 0;
  left: ${props => (props.$fullWidth ? '0' : '50%')};
  right: ${props => (props.$fullWidth ? '0' : 'auto')};
  width: ${props => (props.$fullWidth ? 'auto' : 'min(100vw, 1280px)')};
  transform: ${props => (props.$fullWidth ? 'none' : 'translateX(-50%)')};
  height: ${props => props.$height}px;
  max-height: 50vh;
  overflow-y: auto;
  z-index: 1000;
  display: flex;
  align-items: stretch;
`;

const DismissButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  transform: none;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  color: inherit;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const BannerWidget: React.FC<WidgetRenderProps<Config>> = (
  props: WidgetRenderProps<Config>
): ReactElement => {
  const {
    borderData,
    backgroundData,
    backgroundImage,
    lineColor,
    paddingData,
    placement,
    allowDismiss,
  } = props.widgetState.config;

  const widgetId = props.widgetState.id;
  const sessionKey = `${SESSION_DISMISS_KEY_PREFIX}${widgetId}`;
  const isAlreadyDismissed = getSessionStorageValue(sessionKey) === 'true';

  const [dismissed, setDismissed] = useState(isAlreadyDismissed);

  function background() {
    if (backgroundData.style === 'Color') {
      return backgroundData?.color;
    }
    if (backgroundData.style === 'Image') {
      return `url('${backgroundImage?.url}')`;
    }
    return 'transparent';
  }

  if (
    props.widgetState.config.visible === false ||
    props.widgetState.config.isCustomWidget ||
    // A Preview-session dismissal must not hide the banner in the edit canvas.
    (dismissed && !props.editing)
  ) {
    return <></>;
  }

  const handleDismiss = () => {
    setSessionStorageValue(sessionKey, 'true');
    setDismissed(true);
    // Notify sibling components (e.g. PageLayout) so their own dismiss-derived state updates in sync.
    window.dispatchEvent(
      new CustomEvent('bannerDismissed', { detail: { widgetId } })
    );
  };

  const sharedStyles: React.CSSProperties = {
    background: background(),
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderWidth: `${borderData?.thickness}px`,
    borderTop: `${
      borderData?.isTopBorderVisible ?? true
        ? `${borderData?.thickness}px solid`
        : 'none'
    }`,
    borderBottom: `${
      borderData?.isBottomBorderVisible ?? true
        ? `${borderData?.thickness}px solid`
        : 'none'
    }`,
    borderLeft: `${
      borderData?.isLeftBorderVisible ?? true
        ? `${borderData?.thickness}px solid`
        : 'none'
    }`,
    borderRight: `${
      borderData?.isRightBorderVisible ?? true
        ? `${borderData?.thickness}px solid`
        : 'none'
    }`,
    borderColor: `${lineColor}`,
    borderRadius: `${borderData?.radius}px`,
    backgroundClip: 'content-box',
    boxSizing: 'border-box',
    paddingTop: `${paddingData?.top}px`,
    paddingRight: `${paddingData?.right}px`,
    paddingBottom: `${paddingData?.bottom}px`,
    paddingLeft: `${paddingData?.left}px`,
    height: 'inherit',
  };

  const isSticky = placement === 'Sticky Bottom (Fixed to Screen)';

  // In edit mode the outer WidgetConnectorNew fixed container already handles
  // position:fixed + bottom:0. Rendering another StickyBannerWrapper (position:fixed)
  // here would create a nested fixed element that resolves relative to any CSS
  // transform on the builder canvas, causing full-screen distortion.
  // Just render the background content and let the container control sizing.
  if (isSticky && props.editing) {
    return (
      <StyledBannerWidget
        data-test-id="banner-widget"
        id="bannerWidget"
        className="bannerWidget bannerWidget--sticky"
        style={{
          ...sharedStyles,
          width: '100%',
          height: '100%',
        }}
      />
    );
  }

  if (isSticky) {
    return (
      <StickyBannerWrapper
        $height={props.widgetState.config.height}
        $fullWidth={props.widgetState.config.enableFullScreen}
        data-test-id="banner-widget-sticky-wrapper"
        role="region"
        aria-label="Banner"
        aria-live="polite"
      >
        <StyledBannerWidget
          data-test-id="banner-widget"
          id="bannerWidget"
          className="bannerWidget bannerWidget--sticky"
          style={{ ...sharedStyles, flex: 1, position: 'relative' }}
        >
          {allowDismiss && (
            <DismissButton
              data-test-id="banner-dismiss-btn"
              aria-label="Dismiss banner"
              onClick={handleDismiss}
            >
              &#x2715;
            </DismissButton>
          )}
        </StyledBannerWidget>
      </StickyBannerWrapper>
    );
  }

  return (
    <StyledBannerWidget
      data-test-id="banner-widget"
      id="bannerWidget"
      className="bannerWidget"
      style={sharedStyles}
    />
  );
};
export default BannerWidget;
